// src/utils/axiosInstance.js
import axios from 'axios';
import { saveTokens, getAccessToken, getRefreshToken } from '../utils/authTokens';

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_AUTH_URL, // Base URL from environment
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor to add Authorization header with access token
axiosInstance.interceptors.request.use(
  (config) => {
    const accessToken = getAccessToken();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor to handle 401 errors and refresh token
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Queue the request while refreshing
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = getRefreshToken();
        if (!refreshToken) {
          console.error('❌ No refresh token found');
          window.location.href = '/login';
          return Promise.reject(error);
        }

        const tokenEndpoint = '/authserver/oauth2/token';
        const clientId = 'login-client';
        const clientSecret = 'abc'; // ⚠️ Move to backend in production
        const basicAuth = btoa(`${clientId}:${clientSecret}`);

        const response = await axios.post(
          `${axiosInstance.defaults.baseURL}${tokenEndpoint}`,
          new URLSearchParams({
            grant_type: 'refresh_token',
            client_id: clientId,
            client_secret: clientSecret,
            refresh_token: refreshToken,
          }),
          {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
              Authorization: `Basic ${basicAuth}`,
            },
          }
        );

        const { access_token, refresh_token } = response.data;
        console.log('✅ Refreshed Access Token:', access_token);
        saveTokens(access_token, refresh_token);

        // Update original request with new token
        originalRequest.headers.Authorization = `Bearer ${access_token}`;

        // Resolve queued requests
        processQueue(null, access_token);

        // Retry the original request
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.error('❌ Refresh token error:', refreshError);
        processQueue(refreshError, null);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        // window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;